import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CREATE_WORKSPACE_PATH, PLAN_PATH } from "../../utils/constants/routes";
import { POST } from "../../Apis/apiWrapper";
import { paymentEndpoints } from "../../utils/constants/httpConstants";
import { FullScreenLoader } from "../../components";
import "./onboarding.css";

// Brand-green check for the payment-success popup
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#01A63E" strokeWidth="3" strokeLinecap="round" width="22" height="22" aria-hidden="true" focusable="false">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PaymentSuccessScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  // Confirmed-paid → show the success popup, then advance to workspace setup.
  const [paid, setPaid] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const nextStateRef = useRef(null);
  const trialEndRef = useRef(null);

  const goNext = () => {
    if (!nextStateRef.current) return;
    navigate(CREATE_WORKSPACE_PATH, { replace: true, state: nextStateRef.current });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sessionId = new URLSearchParams(location.search).get("session_id");

      if (!sessionId) {
        navigate(PLAN_PATH, { replace: true });
        return;
      }

      const resume = (() => {
        try { return JSON.parse(sessionStorage.getItem("signup_resume") || "{}"); }
        catch { return {}; }
      })();

      // Poll verify-session with backoff. Right after the Stripe redirect the session can briefly
      // read as not-yet-"paid" (Stripe / webhook timing), so retry a few times before declaring
      // failure — a user who DID pay must never be bounced with "payment was not completed".
      const DELAYS = [0, 1500, 3000, 4500]; // ~9s worst case
      let sawResponse = false;
      let lastErrMsg = "";
      for (let i = 0; i < DELAYS.length; i++) {
        if (cancelled) return;
        if (DELAYS[i]) await new Promise((r) => setTimeout(r, DELAYS[i]));
        try {
          const res = await POST(paymentEndpoints.verifySession, { session_id: sessionId });
          sawResponse = true;
          const data = res?.data?.data;
          // Buy-now completes as "paid". A 7-day trial completes with "no_payment_required"
          // ($0 today) + is_trial — treat both as success so a trial isn't bounced as "not completed".
          const okPaid  = data?.payment_status === "paid";
          const okTrial = data?.payment_status === "no_payment_required" || data?.is_trial === true;
          if (okPaid || okTrial) {
            // Use draft_id from the verify response if available, fall back to the saved one.
            const draftId = data?.signup_draft_id || resume.draft_id;
            sessionStorage.removeItem("signup_resume");
            if (cancelled) return;
            // Trust the BE's is_trial flag (derived from the subscription status). A trial checkout
            // sometimes reports payment_status 'paid' with amount 0, so `okTrial && !okPaid` would
            // wrongly show the "Payment successful" copy — key off is_trial instead.
            const trial = data?.is_trial === true || (okTrial && !okPaid);
            trialEndRef.current = data?.trial_end || null;
            setIsTrial(trial);
            // Show the success popup instead of silently redirecting; auto-advances below.
            nextStateRef.current = { ...resume, draft_id: draftId, paid: true, is_trial: trial, trial_end: data?.trial_end || null };
            setPaid(true);
            return;
          }
          // Got a response but neither paid nor trialing yet → keep polling.
        } catch (err) {
          lastErrMsg = err?.data?.message || lastErrMsg;
        }
      }
      if (cancelled) return;
      if (sawResponse) {
        // Verify responded across all retries but never reported "paid" → genuinely not completed.
        navigate(PLAN_PATH, {
          replace: true,
          state: { error: "Payment was not completed. Please try again." },
        });
      } else {
        // Could never reach verify (every attempt errored) → the user may have been charged.
        setError(lastErrMsg || "Could not verify your payment. Please contact support if you were charged.");
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-advance a moment after the popup shows; "Continue" skips the wait.
  useEffect(() => {
    if (!paid) return;
    const t = setTimeout(goNext, 2800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid]);

  if (error) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "100vh", gap: "16px",
        fontFamily: "'Inter', system-ui, sans-serif", padding: "24px",
      }}>
        <p style={{ color: "#E73F3F", fontWeight: 600, fontSize: "15px", textAlign: "center", maxWidth: "420px" }}>
          {error}
        </p>
        <button
          onClick={() => navigate(PLAN_PATH)}
          style={{
            padding: "10px 24px", cursor: "pointer", borderRadius: "8px",
            border: "1.5px solid #E73F3F", background: "transparent",
            color: "#E73F3F", fontWeight: 600, fontSize: "14px",
          }}
        >
          Return to Plans
        </button>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="onb-modal-overlay">
        <div className="onb-modal" role="dialog" aria-modal="true" aria-label="Payment successful">
          <div className="onb-modal-success">
            <div className="onb-modal-success-icon"><Check /></div>
            <h2 className="onb-modal-title">{isTrial ? "Your 7-day free trial is live!" : "Payment successful!"}</h2>
            <p className="onb-modal-sub">
              {isTrial
                ? <>$0 charged today.{trialEndRef.current ? ` We'll bill you on ${new Date(trialEndRef.current * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} unless you cancel.` : ""} Taking you to workspace setup&hellip;</>
                : <>Your plan is now active. Taking you to workspace setup&hellip;</>}
            </p>
            {/* CTA button removed (2026-07-06) — the modal auto-advances to workspace setup
                via the setTimeout(goNext) effect, so no manual button is shown. */}
          </div>
        </div>
      </div>
    );
  }

  return <FullScreenLoader />;
};

export default PaymentSuccessScreen;
